export type LoadNavigationType = 'hard' | 'spa';

export type LoadMarkDetail = Record<string, string | number | boolean>;

export type LoadSharedPhase = 'route-guards-done';

export type LoadBuiltInPhase =
	| 'navigation-start'
	| 'load-start'
	| 'document-response'
	| 'dom-content-loaded'
	| LoadSharedPhase;

export type LoadPhase<P extends string> = P | LoadBuiltInPhase;

type NoInferPhase<P extends string> = [P][P extends string ? 0 : never];

export type LoadPhaseMarker<P extends string = string> = (phase: P | LoadSharedPhase, detail?: LoadMarkDetail) => void;

export type LoadSegment<P extends string = string> = {
	name: string;
	start: LoadPhase<P>;
	end: LoadPhase<P>;
};

export type LoadMeasureSummary = {
	totalMs: number;
	count: number;
	maxMs: number;
};

export type LoadTimings = {
	navigationType: LoadNavigationType;
	phases: Record<string, number>;
	segments: Record<string, number>;
	measures: Record<string, LoadMeasureSummary>;
	details: LoadMarkDetail;
};

export type LoadTelemetry = {
	properties: Record<string, string>;
	measurements: Record<string, number>;
};

export type LoadReport = LoadTimings & LoadTelemetry;

export type LoadTimingContext = {
	properties?: Record<string, string | number | boolean>;
	measurements?: Record<string, number>;
};

export type LoadTimingOptions<P extends string, M extends string> = {
	/** Prefixes the `<name>:<phase>` marks and names the debug output; instances with the same name share one load. */
	name: string;
	/** Every phase this load may mark besides the shared phases (such as `route-guards-done`). Marks, segments, the required phase and summary phases only accept these names (plus the built-in phases), and debugging warns when an unknown phase is marked. */
	phases: readonly P[];
	/** Named spans between two phases, reported as durations and drawn as `performance.measure` entries when debugging. */
	segments?: LoadSegment<NoInferPhase<P>>[];
	/**
	 * Names of the repeated durations this load may `measure`, each reported as `<name>TotalMs`, `<name>Count` and `<name>MaxMs`.
	 * Debugging warns when an unknown name is measured.
	 */
	measures?: readonly M[];
	/** When set, `complete` returns null until this phase has been marked for the active load. */
	requiredPhase?: NoInferPhase<P>;
	/** Phases whose offsets are summarized in the debug console group label. */
	summaryPhases?: LoadPhase<NoInferPhase<P>>[];
	/** localStorage key that enables the debug report when set to `1`; defaults to `cognito:<name>-perf`. */
	debugKey?: string;
};

export type LoadTiming<P extends string = string, M extends string = string> = {
	/** Starts timing a new load, discarding any load in progress. Hard loads measure from navigation start, SPA loads from this call. */
	begin(navigationType: LoadNavigationType): void;
	/** Marks one of the configured `phases` for the active load. Ignored when no load is active, or when a load id is given that is no longer the active one. */
	mark(phase: P | LoadSharedPhase, detail?: LoadMarkDetail, loadId?: string | null): void;
	/** Captures the active load so marks recorded after asynchronous work are dropped if a newer load has started (or the load ended) in the meantime. */
	marker(): LoadPhaseMarker<P>;
	/**
	 * Records a duration between two `performance.now()` timestamps (`end` defaults to now) toward the active load's totals for `name`.
	 * Ignored when no load is active, or when a load id is given that is no longer the active one.
	 */
	measure(name: M, start: number, end?: number, loadId?: string | null): void;
	/** Identifies the load currently being timed, or null when no load is active. */
	loadId(): string | null;
	/** Indicates whether a load is being timed; phases are only marked while one is. */
	isActive(): boolean;
	/** Indicates whether the given configured phase has already been marked for the active load. */
	hasMark(phase: P | LoadSharedPhase): boolean;
	/** Phase offsets, segment durations, measure totals and mark details recorded so far for the active load, or null when no load is active. */
	timings(): LoadTimings | null;
	/** Telemetry for the phases recorded so far, empty when no load is active or a load id is given that is no longer the active one. */
	pageReadyTelemetry(loadId?: string | null): LoadTelemetry;
	/** Abandons the active load without reporting it. When a load id is given, only that load is abandoned. */
	cancel(loadId?: string | null): void;
	/** Ends the active load and returns its report merged with the given context, or null if the load is not active or has not reached the required phase. */
	complete(context?: LoadTimingContext, loadId?: string | null): LoadReport | null;
};

type StartDetail = { loadId?: unknown; navigationType?: unknown };

function getPerformance(): Performance | null {
	return typeof performance !== 'undefined'
		&& typeof performance.mark === 'function'
		&& typeof performance.getEntriesByName === 'function'
		&& typeof performance.getEntriesByType === 'function'
		? performance
		: null;
}

function createLoadId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
		return crypto.randomUUID();
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function capitalizeAfterSeparators(value: string): string {
	return value.replace(/[^a-zA-Z0-9]+(.)/g, (_, char: string) => char.toUpperCase());
}

/**
 * Flattens timings into Application Insights-style properties and measurements (phase offsets as `<phase>Ms`, measure totals as
 * `<measure>TotalMs`, `<measure>Count` and `<measure>MaxMs`).
 */
export function toLoadTelemetry(timings: LoadTimings): LoadTelemetry {
	const properties: Record<string, string> = { navigationType: timings.navigationType };
	const measurements: Record<string, number> = {};
	for (const [phase, value] of Object.entries(timings.phases)) {
		if (phase !== 'navigation-start')
			measurements[`${capitalizeAfterSeparators(phase)}Ms`] = value;
	}
	for (const [name, summary] of Object.entries(timings.measures)) {
		const key = capitalizeAfterSeparators(name);
		measurements[`${key}TotalMs`] = summary.totalMs;
		measurements[`${key}Count`] = summary.count;
		measurements[`${key}MaxMs`] = summary.maxMs;
	}
	for (const [key, value] of Object.entries(timings.details)) {
		if (typeof value === 'number')
			measurements[key] = value;
		else
			properties[key] = String(value);
	}
	return { properties, measurements };
}

/**
 * Creates a timer for a multi-phase page load backed by `performance.mark`. All state, including the active load id and
 * navigation type, lives on the browser performance timeline, so separately bundled code can time the same load by
 * creating an instance with the same name. Phase names are limited to the given `phases`.
 */
export function createLoadTiming<P extends string, M extends string = never>({
	name,
	phases,
	segments = [],
	measures = [],
	requiredPhase,
	summaryPhases = [],
	debugKey = `cognito:${name}-perf`
}: LoadTimingOptions<P, M>): LoadTiming<P, M> {
	const knownPhases = new Set<string>([...phases, 'route-guards-done']);
	const knownMeasures = new Set<string>(measures);
	const prefix = `${name}:`;
	const startMark = `${prefix}load-start`;
	const measurePrefix = `${name}: `;
	const debugStoreName = `cognito${capitalizeAfterSeparators(`-${name}`)}Perf`;

	function getStart(perf: Performance | null): PerformanceMark | undefined {
		return perf?.getEntriesByName(startMark, 'mark')[0] as PerformanceMark | undefined;
	}

	function isLoadMeasure(entryName: string): boolean {
		return entryName.startsWith(prefix) && !entryName.startsWith(measurePrefix);
	}

	function clearMarks(perf: Performance) {
		for (const mark of perf.getEntriesByType('mark')) {
			if (mark.name.startsWith(prefix))
				perf.clearMarks(mark.name);
		}
		for (const entry of perf.getEntriesByType('measure')) {
			if (isLoadMeasure(entry.name))
				perf.clearMeasures(entry.name);
		}
	}

	function isActive(): boolean {
		return !!getStart(getPerformance());
	}

	function hasMark(phase: P | LoadSharedPhase): boolean {
		return !!getPerformance()?.getEntriesByName(prefix + phase, 'mark').length;
	}

	function loadId(): string | null {
		const id = (getStart(getPerformance())?.detail as StartDetail | null | undefined)?.loadId;
		return typeof id === 'string' ? id : null;
	}

	function begin(navigationType: LoadNavigationType): void {
		const perf = getPerformance();
		if (!perf)
			return;
		clearMarks(perf);
		perf.mark(startMark, { detail: { loadId: createLoadId(), navigationType } });
	}

	function mark(phase: P | LoadSharedPhase, detail?: LoadMarkDetail, id?: string | null): void {
		if (!knownPhases.has(phase) && isDebugEnabled())
			console.warn(`[${name}-perf] unknown phase "${phase}"; add it to the load timing phases`);
		const perf = getPerformance();
		if (!perf || !isActive())
			return;
		if (id !== undefined && id !== loadId())
			return;
		perf.mark(prefix + phase, detail ? { detail } : undefined);
	}

	function marker(): LoadPhaseMarker<P> {
		const id = loadId();
		return (phase, detail) => {
			if (id)
				mark(phase, detail, id);
		};
	}

	function measure(measureName: M, start: number, end?: number, id?: string | null): void {
		if (!knownMeasures.has(measureName) && isDebugEnabled())
			console.warn(`[${name}-perf] unknown measure "${measureName}"; add it to the load timing measures`);
		const perf = getPerformance();
		const loadStart = getStart(perf);
		if (!loadStart || typeof perf.measure !== 'function')
			return;
		if (id !== undefined && id !== (loadStart.detail as StartDetail | null)?.loadId)
			return;
		perf.measure(prefix + measureName, { start, end: end ?? perf.now() });
	}

	function timings(): LoadTimings | null {
		const perf = getPerformance();
		const start = getStart(perf);
		const navigationType = (start?.detail as StartDetail | null | undefined)?.navigationType;
		if (navigationType !== 'hard' && navigationType !== 'spa')
			return null;

		const origin = navigationType === 'hard' ? 0 : start.startTime;
		const phases: Record<string, number> = { 'navigation-start': 0 };
		const details: LoadMarkDetail = {};

		for (const entry of perf.getEntriesByType('mark') as PerformanceMark[]) {
			if (!entry.name.startsWith(prefix) || entry.startTime < start.startTime)
				continue;
			const phase = entry.name.slice(prefix.length);
			if (phase in phases)
				continue;
			phases[phase] = Math.round(entry.startTime - origin);
			if (entry.name !== startMark && entry.detail && typeof entry.detail === 'object') {
				for (const [key, value] of Object.entries(entry.detail as Record<string, unknown>)) {
					if (!(key in details) && ['string', 'number', 'boolean'].includes(typeof value))
						details[key] = value as string | number | boolean;
				}
			}
		}

		if (navigationType === 'hard') {
			const navigation = perf.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
			if (navigation) {
				phases['document-response'] = Math.round(navigation.responseEnd);
				phases['dom-content-loaded'] = Math.round(navigation.domContentLoadedEventEnd);
			}
		}

		const measureSummaries: Record<string, LoadMeasureSummary> = {};
		for (const entry of perf.getEntriesByType('measure')) {
			if (!isLoadMeasure(entry.name) || entry.startTime < start.startTime)
				continue;
			const measureName = entry.name.slice(prefix.length);
			const summary = measureSummaries[measureName] ?? (measureSummaries[measureName] = { totalMs: 0, count: 0, maxMs: 0 });
			summary.totalMs += entry.duration;
			summary.count++;
			summary.maxMs = Math.max(summary.maxMs, entry.duration);
		}
		for (const summary of Object.values(measureSummaries)) {
			summary.totalMs = Math.round(summary.totalMs);
			summary.maxMs = Math.round(summary.maxMs);
		}

		const segmentDurations: Record<string, number> = {};
		for (const segment of segments) {
			if (segment.start in phases && segment.end in phases && phases[segment.end] >= phases[segment.start])
				segmentDurations[segment.name] = phases[segment.end] - phases[segment.start];
		}

		return { navigationType, phases, segments: segmentDurations, measures: measureSummaries, details };
	}

	function pageReadyTelemetry(id?: string | null): LoadTelemetry {
		const current = id === undefined || id === loadId() ? timings() : null;
		return current ? toLoadTelemetry(current) : { properties: {}, measurements: {} };
	}

	function cancel(id?: string | null): void {
		if (id !== undefined && id !== loadId())
			return;
		const perf = getPerformance();
		if (perf)
			clearMarks(perf);
	}

	function isDebugEnabled(): boolean {
		try {
			return localStorage.getItem(debugKey) === '1';
		}
		catch {
			return false;
		}
	}

	function recordMeasures(perf: Performance, report: LoadTimings, loadStart: number) {
		if (typeof perf.measure !== 'function')
			return;
		const offset = report.navigationType === 'hard' ? 0 : loadStart;
		for (const segment of segments)
			perf.clearMeasures(measurePrefix + segment.name);
		for (const segment of segments) {
			if (report.segments[segment.name] === undefined)
				continue;
			try {
				perf.measure(measurePrefix + segment.name, { start: offset + report.phases[segment.start], end: offset + report.phases[segment.end] });
			}
			catch {
				return;
			}
		}
	}

	function logReport(report: LoadReport) {
		const win = globalThis as unknown as Record<string, { last: LoadReport; history: LoadReport[] }>;
		const store = win[debugStoreName] ?? { last: null, history: [] };
		store.last = report;
		store.history.push(report);
		win[debugStoreName] = store;

		const summary = summaryPhases.map(phase => `${phase.replace(/-/g, ' ')} ${report.phases[phase]}ms`).join(', ');
		console.groupCollapsed(`[${name}-perf] ${report.navigationType} load${summary ? `: ${summary}` : ''}`);
		console.table(Object.fromEntries(segments.filter(s => report.segments[s.name] !== undefined).map(s => [s.name, { ms: report.segments[s.name], from: s.start, to: s.end }])));
		console.table(report.phases);
		console.log(report.properties, report.measurements);
		console.groupEnd();
	}

	function complete(context: LoadTimingContext = {}, id?: string | null): LoadReport | null {
		if (id !== undefined && id !== loadId())
			return null;
		const current = timings();
		if (!current || (requiredPhase && !(requiredPhase in current.phases)))
			return null;

		const perf = getPerformance();
		const telemetry = toLoadTelemetry(current);
		const properties: Record<string, string> = { ...telemetry.properties };
		for (const [key, value] of Object.entries(context.properties ?? {}))
			properties[key] = String(value);
		const measurements = { ...telemetry.measurements, ...context.measurements };
		const report: LoadReport = { ...current, properties, measurements };

		if (isDebugEnabled()) {
			recordMeasures(perf, current, getStart(perf)?.startTime ?? 0);
			logReport(report);
		}

		clearMarks(perf);
		return report;
	}

	return { begin, mark, marker, measure, loadId, isActive, hasMark, timings, pageReadyTelemetry, cancel, complete };
}
